// src/components/ArticleCard.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import useTranslationStatus from "../hooks/useTranslationStatus";

/* ═══════════════════════════════════════════
   CONFIG / HELPERS
═══════════════════════════════════════════ */

const API_BASE   = process.env.REACT_APP_API_BASE   || "http://localhost:8000/api";
const MEDIA_BASE = process.env.REACT_APP_MEDIA_BASE || API_BASE.replace(/\/api\/?$/i, "");
const STORAGE_LANG_KEY = "site_lang";

const resolveUrl = (u) => {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return `${MEDIA_BASE}${u}`;
  return `${MEDIA_BASE}/${u}`;
};

const svgPlaceholder = (w, h, text = "") => {
  const fs  = Math.round(Math.min(w, h) / 5);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><rect width="100%" height="100%" fill="#f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#d1d5db" font-family="system-ui,sans-serif" font-size="${fs}">${text}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const FALLBACK_IMG = (w = 640, h = 360) => svgPlaceholder(w, h);

const formatDate = (d, lang = "en") => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  try {
    return dt.toLocaleDateString(lang, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return dt.toLocaleDateString();
  }
};

const timeAgo = (d) => {
  if (!d) return "";
  const secs = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (secs < 60)   return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
  return "";
};

const estimateReadingTime = (text = "", wpm = 200) => {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  if (!words) return null;
  return `${Math.max(1, Math.round(words / wpm))} min`;
};

const pickCoverImage = (a) => {
  const c = [a?.image, a?.cover, a?.cover_image, a?.coverImage, a?.images?.cover, a?.media?.cover?.url, a?.image_sizes?.large].filter(Boolean);
  return c.length ? resolveUrl(c[0]) : null;
};

const pickThumbImage = (a) => {
  const c = [a?.thumbnail, a?.thumb, a?.image_small, a?.small_image, a?.thumbnail_url, a?.images?.thumb, a?.media?.thumb?.url].filter(Boolean);
  if (c.length) return resolveUrl(c[0]);
  return pickCoverImage(a);
};

function parseJwt(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(
      Array.prototype.map.call(atob(base64), (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join("")
    ));
  } catch { return null; }
}

const LANG_DIR = (l) => l === "ar" || l === "he" || l === "fa" ? "rtl" : "ltr";

/* ═══════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════ */

/**
 * Props
 * ─────
 * article     — backend article object (required)
 * onBookmark  — (article) => void
 * variant     — "card" | "list" | "hero" | "compact"  (default "card")
 * bookmarked  — boolean
 */
const ArticleCard = ({
  article,
  onBookmark,
  variant    = "card",
  bookmarked = false,
}) => {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem(STORAGE_LANG_KEY) || "en"; } catch { return "en"; }
  });

  const [imgError,    setImgError]    = useState(false);
  const [thumbError,  setThumbError]  = useState(false);
  const [bookState,   setBookState]   = useState(bookmarked);
  const [bookAnim,    setBookAnim]    = useState(false);

  /* ── auth ── */
  const token = useMemo(() => {
    try { return localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken") || null; } catch { return null; }
  }, []);

  const isStaff = useMemo(() => {
    if (!token) return false;
    const p = parseJwt(token);
    if (!p) return false;
    return !!(p.is_staff || p.staff ||
      (p.role && ["staff","admin"].includes(String(p.role).toLowerCase())) ||
      (Array.isArray(p.roles) && p.roles.includes("staff")));
  }, [token]);

  /* ── translation hook ── */
  const slug       = article?.slug ?? article?.id ?? null;
  const sourceLang = (article?.lang || "").toLowerCase() || null;

  const {
    translation: hookTranslation,
    loading:     loadingTr,
    error:       trError,
    requesting:  trRequesting,
    requestTranslation: hookRequestTranslation,
  } = useTranslationStatus(slug, lang, {
    pollOnPending:    true,
    pollInterval:     2500,
    authToken:        token,
    withCredentials:  false,
  });

  const translation = hookTranslation;

  /* ── resolved display fields ── */
  const displayed = useMemo(() => {
    if (translation?.status === "done") {
      return {
        title:   translation.title   || article.title   || "Untitled",
        excerpt: translation.excerpt || article.excerpt || "",
        content: translation.content || article.content || "",
      };
    }
    if (translation?.title && translation?.status) {
      return {
        title:   translation.title   || article.title   || "Untitled",
        excerpt: translation.excerpt || article.excerpt || "",
        content: translation.content || article.content || "",
      };
    }
    return {
      title:   article.title   || "Untitled",
      excerpt: article.excerpt || "",
      content: article.content || "",
    };
  }, [translation, article]);

  /* ── language events ── */
  useEffect(() => {
    const onStorage = (e) => { if (e.key === STORAGE_LANG_KEY && e.newValue) setLang(e.newValue); };
    const onCustom  = (e) => { const l = e?.detail?.lang; if (l) setLang(l); };
    window.addEventListener("storage",              onStorage);
    window.addEventListener("site:language_changed", onCustom);
    window.addEventListener("site:set_lang",         onCustom);
    return () => {
      window.removeEventListener("storage",              onStorage);
      window.removeEventListener("site:language_changed", onCustom);
      window.removeEventListener("site:set_lang",         onCustom);
    };
  }, []);

  /* ── bookmark ── */
  const handleBookmark = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBookState((s) => !s);
    setBookAnim(true);
    setTimeout(() => setBookAnim(false), 600);
    onBookmark?.(article);
  };

  if (!article) return null;

  /* ── derived values ── */
  const dir         = LANG_DIR(lang);
  const isRTL       = dir === "rtl";
  const href        = `/${lang}/articles/${article.slug ?? article.id}`;
  const title       = displayed.title;
  const excerpt     = displayed.excerpt;
  const date        = article.published_at || article.created_at || null;
  const ago         = timeAgo(date);
  const authorName  = article.author?.user?.username || article.author?.username || article.author_name || null;
  const authorAvatar = article.author?.avatar || article.author?.user?.avatar || null;
  const readingTime = article.reading_time || estimateReadingTime(displayed.content || excerpt || "");
  const categories  = Array.isArray(article.categories) ? article.categories : [];
  const tags        = Array.isArray(article.tags) ? article.tags : [];
  const views       = typeof article.views === "number" ? article.views : null;
  const coverSrc    = imgError   ? null : pickCoverImage(article);
  const thumbSrc    = thumbError ? null : pickThumbImage(article);

  const needsTranslation = lang && sourceLang && sourceLang.toLowerCase() !== lang.toLowerCase();
  const trDone     = translation?.status === "done";
  const trPending  = translation?.status === "pending";
  const trMissing  = !translation || translation?.status === "missing";

  /* ── read-more label ── */
  const readMoreLabel = {
    en: "Read more", fr: "Lire la suite", ar: "اقرأ المزيد", de: "Weiterlesen",
  }[lang] || "Read more";

  /* ════════════════════════════════════════
     HERO VARIANT
  ════════════════════════════════════════ */
  if (variant === "hero") {
    return (
      <>
        <article className={`ac-hero${isRTL ? " ac-rtl" : ""}`} dir={dir}>
          <Link to={href} className="ac-hero-img-wrap" aria-label={title}>
            {coverSrc
              ? <img src={coverSrc} alt={title} loading="eager" className="ac-hero-img" onError={() => setImgError(true)} />
              : <div className="ac-hero-img ac-hero-img--fallback"><i className="bi bi-newspaper" /></div>
            }
            <div className="ac-hero-overlay" />
          </Link>
          <div className="ac-hero-body">
            {categories.length > 0 && (
              <div className="ac-cats">
                {categories.slice(0, 2).map((c) => (
                  <Link key={c.id || c.slug} to={`/${lang}/category/${c.slug ?? c.id}`} className="ac-cat-badge ac-cat-badge--hero">
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
            <Link to={href} className="ac-hero-title-link">
              <h2 className="ac-hero-title">{title}</h2>
            </Link>
            {excerpt && <p className="ac-hero-excerpt">{excerpt}</p>}
            <div className="ac-meta ac-meta--hero">
              {authorName && <span className="ac-meta-item"><i className="bi bi-person-fill" />{authorName}</span>}
              {ago        && <span className="ac-meta-item"><i className="bi bi-clock"       />{ago}</span>}
              {readingTime && <span className="ac-meta-item ac-reading"><i className="bi bi-book" />{readingTime} read</span>}
            </div>
            {trDone && <span className="ac-tr-pill ac-tr-pill--done"><i className="bi bi-translate" />{lang.toUpperCase()}</span>}
          </div>
          <button
            type="button"
            className={`ac-bookmark-btn${bookState ? " ac-bookmark-btn--on" : ""}${bookAnim ? " ac-bookmark-btn--anim" : ""}`}
            onClick={handleBookmark}
            aria-label={bookState ? "Remove bookmark" : "Bookmark"}
          >
            <i className={`bi ${bookState ? "bi-bookmark-fill" : "bi-bookmark"}`} />
          </button>
        </article>
        <ArticleCardStyles />
      </>
    );
  }

  /* ════════════════════════════════════════
     LIST VARIANT
  ════════════════════════════════════════ */
  if (variant === "list") {
    return (
      <>
        <article className={`ac-list${isRTL ? " ac-rtl" : ""}`} dir={dir}>
          <Link to={href} className="ac-list-thumb-wrap" aria-label={title}>
            {thumbSrc
              ? <img src={thumbSrc} alt="" loading="lazy" className="ac-list-thumb" onError={() => setThumbError(true)} />
              : <div className="ac-list-thumb ac-list-thumb--fallback"><i className="bi bi-image" /></div>
            }
            {trDone && <span className="ac-list-tr-dot" title={`Translated (${lang})`} />}
          </Link>

          <div className="ac-list-body">
            {categories.length > 0 && (
              <div className="ac-cats ac-cats--xs">
                <Link to={`/${lang}/category/${categories[0].slug ?? categories[0].id}`} className="ac-cat-badge">
                  {categories[0].name}
                </Link>
              </div>
            )}
            <Link to={href} className="ac-list-title-link">
              <h3 className="ac-list-title">{title}</h3>
            </Link>
            <div className="ac-meta">
              {authorName  && <span className="ac-meta-item"><i className="bi bi-person" />{authorName}</span>}
              {ago         && <span className="ac-meta-item">{ago}</span>}
              {readingTime && <span className="ac-meta-item ac-reading"><i className="bi bi-clock" />{readingTime}</span>}
              {views !== null && <span className="ac-meta-item"><i className="bi bi-eye" />{views.toLocaleString()}</span>}
            </div>
          </div>

          <div className="ac-list-actions">
            <button
              type="button"
              className={`ac-bookmark-btn ac-bookmark-btn--sm${bookState ? " ac-bookmark-btn--on" : ""}${bookAnim ? " ac-bookmark-btn--anim" : ""}`}
              onClick={handleBookmark}
              aria-label={bookState ? "Remove bookmark" : "Bookmark"}
            >
              <i className={`bi ${bookState ? "bi-bookmark-fill" : "bi-bookmark"}`} />
            </button>
          </div>
        </article>
        <ArticleCardStyles />
      </>
    );
  }

  /* ════════════════════════════════════════
     COMPACT VARIANT
  ════════════════════════════════════════ */
  if (variant === "compact") {
    return (
      <>
        <article className={`ac-compact${isRTL ? " ac-rtl" : ""}`} dir={dir}>
          <Link to={href} className="ac-compact-thumb-wrap" aria-label={title}>
            {thumbSrc
              ? <img src={thumbSrc} alt="" loading="lazy" className="ac-compact-thumb" onError={() => setThumbError(true)} />
              : <div className="ac-compact-thumb ac-compact-thumb--fallback"><i className="bi bi-image" /></div>
            }
          </Link>
          <div className="ac-compact-body">
            {categories.length > 0 && (
              <Link to={`/${lang}/category/${categories[0].slug ?? categories[0].id}`} className="ac-compact-cat">
                {categories[0].name}
              </Link>
            )}
            <Link to={href} className="ac-compact-title-link">
              <h4 className="ac-compact-title">{title}</h4>
            </Link>
            {ago && <span className="ac-compact-meta">{ago}</span>}
          </div>
        </article>
        <ArticleCardStyles />
      </>
    );
  }

  /* ════════════════════════════════════════
     DEFAULT CARD VARIANT
  ════════════════════════════════════════ */
  return (
    <>
      <article className={`ac-card${isRTL ? " ac-rtl" : ""}`} dir={dir}>

        {/* Cover image */}
        <Link to={href} className="ac-card-cover-wrap" aria-label={title} tabIndex={-1}>
          {coverSrc
            ? <img src={coverSrc} alt={title} loading="lazy" className="ac-card-cover" onError={() => setImgError(true)} />
            : <div className="ac-card-cover ac-card-cover--fallback"><i className="bi bi-newspaper" /></div>
          }
          {/* Category pill on cover */}
          {categories.length > 0 && (
            <Link
              to={`/${lang}/category/${categories[0].slug ?? categories[0].id}`}
              className="ac-card-cat-overlay"
              onClick={(e) => e.stopPropagation()}
            >
              {categories[0].name}
            </Link>
          )}
          {/* Reading time badge */}
          {readingTime && (
            <span className="ac-card-rt-badge">
              <i className="bi bi-clock" />{readingTime}
            </span>
          )}
        </Link>

        {/* Body */}
        <div className="ac-card-body">

          {/* Extra categories (overflow) */}
          {categories.length > 1 && (
            <div className="ac-cats mb-2">
              {categories.slice(1, 4).map((c) => (
                <Link key={c.id || c.slug} to={`/${lang}/category/${c.slug ?? c.id}`} className="ac-cat-badge">
                  {c.name}
                </Link>
              ))}
              {categories.length > 4 && <span className="ac-cat-badge ac-cat-badge--more">+{categories.length - 4}</span>}
            </div>
          )}

          {/* Title */}
          <Link to={href} className="ac-card-title-link">
            <h3 className="ac-card-title">{title}</h3>
          </Link>

          {/* Translation status */}
          {needsTranslation && (
            <div className="ac-tr-status">
              {loadingTr && (
                <span className="ac-tr-pill ac-tr-pill--loading">
                  <span className="ac-tr-spinner" />{lang.toUpperCase()}
                </span>
              )}
              {!loadingTr && trDone && (
                <span className="ac-tr-pill ac-tr-pill--done">
                  <i className="bi bi-translate" />Translated · {lang.toUpperCase()}
                </span>
              )}
              {!loadingTr && trPending && (
                <span className="ac-tr-pill ac-tr-pill--pending">
                  <span className="ac-tr-spinner" />Translating…
                </span>
              )}
              {!loadingTr && trMissing && (
                <span className="ac-tr-pill ac-tr-pill--missing">
                  <i className="bi bi-globe" />No translation
                </span>
              )}
            </div>
          )}

          {/* Excerpt */}
          {excerpt && <p className="ac-card-excerpt">{excerpt}</p>}

          {/* Tags */}
          {tags.length > 0 && (
            <div className="ac-tags">
              {tags.slice(0, 3).map((tg) => (
                <Link key={tg.id || tg.slug} to={`/${lang}/tag/${tg.slug ?? tg.id}`} className="ac-tag">
                  #{tg.name}
                </Link>
              ))}
            </div>
          )}

          {/* Footer row */}
          <div className="ac-card-footer">
            {/* Author */}
            <div className="ac-author">
              <div className="ac-author-avatar">
                {authorAvatar
                  ? <img src={resolveUrl(authorAvatar)} alt={authorName || ""} loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
                  : null}
                <span className="ac-author-initials" style={{ display: authorAvatar ? "none" : "flex" }}>
                  {authorName ? authorName[0].toUpperCase() : "?"}
                </span>
              </div>
              <div className="ac-author-info">
                {authorName && <span className="ac-author-name">{authorName}</span>}
                <span className="ac-author-date">
                  {ago || formatDate(date, lang)}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="ac-card-actions">
              {views !== null && (
                <span className="ac-views">
                  <i className="bi bi-eye" />{views >= 1000 ? `${(views / 1000).toFixed(1)}k` : views}
                </span>
              )}
              <button
                type="button"
                className={`ac-bookmark-btn${bookState ? " ac-bookmark-btn--on" : ""}${bookAnim ? " ac-bookmark-btn--anim" : ""}`}
                onClick={handleBookmark}
                aria-label={bookState ? "Remove bookmark" : "Bookmark"}
                title={bookState ? "Remove bookmark" : "Bookmark this article"}
              >
                <i className={`bi ${bookState ? "bi-bookmark-fill" : "bi-bookmark"}`} />
              </button>
            </div>
          </div>

          {/* Read more + translation request */}
          <div className="ac-card-bottom">
            <Link to={href} className="ac-read-more">
              {readMoreLabel}
              <i className={`bi ${isRTL ? "bi-arrow-left" : "bi-arrow-right"} ac-read-more-arrow`} />
            </Link>

            {/* Translation request (only for staff or when missing) */}
            {needsTranslation && !loadingTr && trMissing && (
              <button
                type="button"
                className="ac-tr-request-btn"
                onClick={async () => {
                  try { await hookRequestTranslation(); }
                  catch (err) { console.error("requestTranslation error", err); }
                }}
                disabled={trRequesting}
                title={isStaff ? undefined : "You may need staff permissions"}
              >
                {trRequesting
                  ? <><span className="ac-tr-spinner ac-tr-spinner--dark" />Requesting…</>
                  : <><i className="bi bi-translate" />Translate</>
                }
              </button>
            )}
          </div>

          {trError && (
            <div className="ac-tr-error">
              <i className="bi bi-exclamation-circle" />
              {typeof trError === "string" ? trError : "Translation request failed"}
            </div>
          )}
        </div>
      </article>
      <ArticleCardStyles />
    </>
  );
};

/* ═══════════════════════════════════════════
   STYLES (injected once via a stable component)
═══════════════════════════════════════════ */
let _stylesInjected = false;
const ArticleCardStyles = () => {
  useEffect(() => {
    if (_stylesInjected) return;
    _stylesInjected = true;
    const el = document.createElement("style");
    el.id    = "ac-styles";
    el.textContent = AC_CSS;
    document.head.appendChild(el);
    return () => {};
  }, []);
  return null;
};

const AC_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Noto+Sans+Arabic:wght@400;500;600;700&display=swap');

/* ── tokens ── */
:root {
  --ac-font:    'Sora','Helvetica Neue',sans-serif;
  --ac-font-ar: 'Noto Sans Arabic','Arabic UI Text',Tahoma,sans-serif;
  --ac-red:     #e63311;
  --ac-ink:     #111;
  --ac-ink2:    #6b7280;
  --ac-ink3:    #9ca3af;
  --ac-bg:      #fff;
  --ac-bg2:     #f7f8f9;
  --ac-border:  rgba(0,0,0,.07);
  --ac-sh-sm:   0 1px 3px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04);
  --ac-sh-md:   0 4px 16px rgba(0,0,0,.08), 0 2px 6px rgba(0,0,0,.04);
  --ac-sh-lg:   0 12px 40px rgba(0,0,0,.12), 0 4px 12px rgba(0,0,0,.06);
  --ac-r:       12px;
  --ac-t:       0.2s cubic-bezier(.4,0,.2,1);
}

/* ── scope ── */
.ac-card, .ac-hero, .ac-list, .ac-compact { font-family: var(--ac-font); color: var(--ac-ink); box-sizing: border-box; }
.ac-card *, .ac-hero *, .ac-list *, .ac-compact * { box-sizing: border-box; }
.ac-rtl { direction: rtl; }
.ac-rtl * { font-family: var(--ac-font-ar); }

/* ════════════════════════════════════════
   DEFAULT CARD
════════════════════════════════════════ */
.ac-card {
  background: var(--ac-bg);
  border-radius: var(--ac-r);
  border: 1px solid var(--ac-border);
  box-shadow: var(--ac-sh-sm);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: box-shadow var(--ac-t), transform var(--ac-t);
  position: relative;
}
.ac-card:hover { box-shadow: var(--ac-sh-lg); transform: translateY(-2px); }

/* Cover */
.ac-card-cover-wrap {
  display: block;
  position: relative;
  overflow: hidden;
  aspect-ratio: 16/9;
  background: var(--ac-bg2);
  flex-shrink: 0;
}
.ac-card-cover {
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.5s ease;
}
.ac-card:hover .ac-card-cover { transform: scale(1.04); }
.ac-card-cover--fallback {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  color: #d1d5db; font-size: 2rem;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
}
.ac-card-cat-overlay {
  position: absolute; top: 12px;
  left: 12px;
  display: inline-flex; align-items: center;
  background: var(--ac-red); color: #fff;
  font-size: 0.6rem; font-weight: 700;
  letter-spacing: .07em; text-transform: uppercase;
  padding: 4px 9px; border-radius: 4px;
  text-decoration: none;
  transition: background var(--ac-t);
  z-index: 1;
}
.ac-rtl .ac-card-cat-overlay { left: auto; right: 12px; }
.ac-card-cat-overlay:hover { background: #c4280d; color: #fff; }
.ac-card-rt-badge {
  position: absolute; bottom: 10px;
  right: 10px;
  display: inline-flex; align-items: center; gap: 4px;
  background: rgba(0,0,0,.62); color: #fff;
  font-size: 0.6rem; font-weight: 600;
  padding: 3px 8px; border-radius: 4px;
  backdrop-filter: blur(4px);
}
.ac-rtl .ac-card-rt-badge { right: auto; left: 10px; }

/* Body */
.ac-card-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 16px;
  gap: 8px;
}

/* Title */
.ac-card-title-link { text-decoration: none; display: block; }
.ac-card-title {
  font-size: 1rem; font-weight: 700; line-height: 1.45;
  color: var(--ac-ink); margin: 0;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
  transition: color var(--ac-t);
}
.ac-card-title-link:hover .ac-card-title { color: var(--ac-red); }
@media (min-width: 768px) { .ac-card-title { font-size: 1.0625rem; } }

/* Excerpt */
.ac-card-excerpt {
  font-size: 0.8125rem; line-height: 1.65; color: var(--ac-ink2);
  margin: 0;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
  flex: 1;
}

/* Tags */
.ac-tags { display: flex; flex-wrap: wrap; gap: 5px; }
.ac-tag {
  font-size: 0.65rem; font-weight: 600; color: var(--ac-ink2);
  background: var(--ac-bg2); border: 1px solid var(--ac-border);
  padding: 2px 8px; border-radius: 100px;
  text-decoration: none;
  transition: border-color var(--ac-t), color var(--ac-t), background var(--ac-t);
}
.ac-tag:hover { color: var(--ac-red); border-color: var(--ac-red); background: #fef2f2; }

/* Categories */
.ac-cats { display: flex; flex-wrap: wrap; gap: 5px; }
.ac-cats--xs .ac-cat-badge { font-size: 0.6rem; padding: 2px 7px; }
.ac-cat-badge {
  display: inline-flex; align-items: center;
  font-size: 0.65rem; font-weight: 700; letter-spacing: .05em; text-transform: uppercase;
  color: var(--ac-red); background: rgba(230,51,17,.07);
  padding: 3px 8px; border-radius: 4px;
  text-decoration: none;
  transition: background var(--ac-t), color var(--ac-t);
}
.ac-cat-badge:hover { background: var(--ac-red); color: #fff; }
.ac-cat-badge--hero { background: rgba(255,255,255,.15); color: #fff; font-size: 0.65rem; }
.ac-cat-badge--hero:hover { background: rgba(255,255,255,.3); color: #fff; }
.ac-cat-badge--more { color: var(--ac-ink3); background: var(--ac-bg2); }

/* Footer */
.ac-card-footer {
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px; padding-top: 10px;
  border-top: 1px solid var(--ac-border);
  margin-top: auto;
}

/* Author */
.ac-author { display: flex; align-items: center; gap: 8px; min-width: 0; }
.ac-author-avatar {
  width: 30px; height: 30px; border-radius: 50%;
  overflow: hidden; flex-shrink: 0; position: relative;
  background: var(--ac-bg2);
}
.ac-author-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ac-author-initials {
  width: 100%; height: 100%;
  background: linear-gradient(135deg, var(--ac-red), #ff6b35);
  color: #fff; font-size: 0.6875rem; font-weight: 800;
  align-items: center; justify-content: center;
  border-radius: 50%;
}
.ac-author-info { display: flex; flex-direction: column; min-width: 0; }
.ac-author-name { font-size: 0.75rem; font-weight: 700; color: var(--ac-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1.2; }
.ac-author-date { font-size: 0.65rem; color: var(--ac-ink3); }

/* Card actions */
.ac-card-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.ac-views { display: inline-flex; align-items: center; gap: 4px; font-size: 0.72rem; color: var(--ac-ink3); }

/* Bottom row */
.ac-card-bottom {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
}

/* Read more */
.ac-read-more {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.79rem; font-weight: 700; color: var(--ac-red);
  text-decoration: none;
  transition: gap var(--ac-t);
}
.ac-read-more:hover { color: #c4280d; }
.ac-read-more:hover .ac-read-more-arrow { transform: translateX(3px); }
.ac-rtl .ac-read-more:hover .ac-read-more-arrow { transform: translateX(-3px); }
.ac-read-more-arrow { transition: transform var(--ac-t); }

/* Translation request button */
.ac-tr-request-btn {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.72rem; font-weight: 600;
  color: var(--ac-ink2); background: var(--ac-bg2);
  border: 1px solid var(--ac-border); border-radius: 6px;
  padding: 4px 10px; cursor: pointer;
  font-family: var(--ac-font);
  transition: border-color var(--ac-t), color var(--ac-t), background var(--ac-t);
}
.ac-tr-request-btn:hover:not(:disabled) { border-color: var(--ac-red); color: var(--ac-red); background: #fef2f2; }
.ac-tr-request-btn:disabled { opacity: .55; cursor: not-allowed; }

/* Bookmark button */
.ac-bookmark-btn {
  width: 32px; height: 32px;
  background: var(--ac-bg2); border: 1px solid var(--ac-border);
  border-radius: 8px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.875rem; color: var(--ac-ink3);
  flex-shrink: 0;
  transition: background var(--ac-t), color var(--ac-t), border-color var(--ac-t), transform 0.15s;
}
.ac-bookmark-btn--sm { width: 28px; height: 28px; font-size: 0.8rem; }
.ac-bookmark-btn:hover { background: #fef2f2; color: var(--ac-red); border-color: rgba(230,51,17,.2); }
.ac-bookmark-btn--on { background: #fef2f2; color: var(--ac-red); border-color: rgba(230,51,17,.25); }
.ac-bookmark-btn--anim { transform: scale(1.35); }

/* Translation status pills */
.ac-tr-status { display: flex; align-items: center; }
.ac-tr-pill {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.65rem; font-weight: 700; letter-spacing: .03em;
  padding: 3px 9px; border-radius: 100px;
}
.ac-tr-pill--done    { background: #dcfce7; color: #16a34a; }
.ac-tr-pill--pending { background: #fef9c3; color: #a16207; }
.ac-tr-pill--missing { background: var(--ac-bg2); color: var(--ac-ink3); }
.ac-tr-pill--loading { background: var(--ac-bg2); color: var(--ac-ink3); }

/* Translation error */
.ac-tr-error {
  display: flex; align-items: center; gap: 5px;
  font-size: 0.72rem; color: #dc2626;
  background: #fef2f2; border: 1px solid #fca5a5;
  border-radius: 6px; padding: 5px 10px; margin-top: 4px;
}

/* Spinner */
.ac-tr-spinner {
  display: inline-block;
  width: 10px; height: 10px; border-radius: 50%;
  border: 1.5px solid currentColor; border-top-color: transparent;
  animation: ac-spin .65s linear infinite;
  flex-shrink: 0;
}
.ac-tr-spinner--dark { border-color: var(--ac-ink2); border-top-color: transparent; }
@keyframes ac-spin { to { transform: rotate(360deg); } }

/* Meta row */
.ac-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.ac-meta-item {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.72rem; color: var(--ac-ink3);
}
.ac-reading { color: var(--ac-ink2); font-weight: 600; }
.ac-meta--hero .ac-meta-item { color: rgba(255,255,255,.75); }
.ac-meta--hero .ac-reading { color: rgba(255,255,255,.9); }

/* ════════════════════════════════════════
   HERO VARIANT
════════════════════════════════════════ */
.ac-hero {
  position: relative;
  border-radius: var(--ac-r);
  overflow: hidden;
  aspect-ratio: 16/9;
  min-height: 300px;
  background: #111;
  display: block;
  transition: box-shadow var(--ac-t);
}
.ac-hero:hover { box-shadow: var(--ac-sh-lg); }

.ac-hero-img-wrap { display: block; position: absolute; inset: 0; }
.ac-hero-img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.6s ease; }
.ac-hero:hover .ac-hero-img { transform: scale(1.03); }
.ac-hero-img--fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #1f2937; color: #4b5563; font-size: 3rem; }
.ac-hero-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,.88) 0%, rgba(0,0,0,.4) 40%, rgba(0,0,0,.05) 70%);
}

.ac-hero-body {
  position: absolute; bottom: 0; left: 0; right: 0;
  padding: clamp(16px, 4vw, 28px);
  display: flex; flex-direction: column; gap: 8px;
  z-index: 2;
}

.ac-hero-title-link { text-decoration: none; }
.ac-hero-title {
  font-size: clamp(1rem, 3vw, 1.5rem);
  font-weight: 800; line-height: 1.3; color: #fff;
  margin: 0;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
  transition: color var(--ac-t);
  text-shadow: 0 1px 3px rgba(0,0,0,.3);
}
.ac-hero-title-link:hover .ac-hero-title { color: #fde047; }

.ac-hero-excerpt {
  font-size: 0.8125rem; color: rgba(255,255,255,.75); line-height: 1.6; margin: 0;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}

.ac-hero .ac-bookmark-btn {
  position: absolute; top: 14px; right: 14px;
  background: rgba(0,0,0,.45); border-color: rgba(255,255,255,.2);
  color: rgba(255,255,255,.7); backdrop-filter: blur(6px);
  z-index: 3;
}
.ac-rtl .ac-hero .ac-bookmark-btn { right: auto; left: 14px; }
.ac-hero .ac-bookmark-btn:hover { background: rgba(230,51,17,.8); color: #fff; border-color: transparent; }
.ac-hero .ac-bookmark-btn--on { background: rgba(230,51,17,.75); color: #fff; }

.ac-hero .ac-tr-pill--done {
  position: absolute; top: 14px; left: 14px;
  background: rgba(22,163,74,.85); color: #fff;
  backdrop-filter: blur(4px); z-index: 3;
}
.ac-rtl .ac-hero .ac-tr-pill--done { left: auto; right: 52px; }

/* ════════════════════════════════════════
   LIST VARIANT
════════════════════════════════════════ */
.ac-list {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 12px;
  border-radius: var(--ac-r);
  border: 1px solid var(--ac-border);
  background: var(--ac-bg);
  box-shadow: var(--ac-sh-sm);
  transition: box-shadow var(--ac-t), transform var(--ac-t);
  position: relative;
}
.ac-list:hover { box-shadow: var(--ac-sh-md); transform: translateY(-1px); }

.ac-list-thumb-wrap {
  flex-shrink: 0; display: block; position: relative;
  width: 88px; height: 64px; border-radius: 8px; overflow: hidden;
  background: var(--ac-bg2);
}
@media (min-width: 480px) { .ac-list-thumb-wrap { width: 110px; height: 78px; } }
.ac-list-thumb { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.4s ease; }
.ac-list:hover .ac-list-thumb { transform: scale(1.06); }
.ac-list-thumb--fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #d1d5db; font-size: 1.25rem; background: linear-gradient(135deg, #f3f4f6, #e5e7eb); }
.ac-list-tr-dot {
  position: absolute; bottom: 5px; right: 5px;
  width: 7px; height: 7px; border-radius: 50%;
  background: #16a34a; border: 1.5px solid #fff;
}

.ac-list-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.ac-list-title-link { text-decoration: none; }
.ac-list-title {
  font-size: 0.9375rem; font-weight: 700; line-height: 1.4; color: var(--ac-ink); margin: 0;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  transition: color var(--ac-t);
}
.ac-list-title-link:hover .ac-list-title { color: var(--ac-red); }

.ac-list-actions { display: flex; align-items: center; flex-shrink: 0; }

/* ════════════════════════════════════════
   COMPACT VARIANT
════════════════════════════════════════ */
.ac-compact {
  display: flex; align-items: center; gap: 10px;
  padding: 8px;
  border-radius: 8px;
  background: var(--ac-bg);
  transition: background var(--ac-t);
  text-decoration: none;
}
.ac-compact:hover { background: var(--ac-bg2); }

.ac-compact-thumb-wrap {
  flex-shrink: 0; display: block;
  width: 60px; height: 44px;
  border-radius: 6px; overflow: hidden;
  background: var(--ac-bg2);
}
.ac-compact-thumb { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.35s ease; }
.ac-compact:hover .ac-compact-thumb { transform: scale(1.08); }
.ac-compact-thumb--fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #d1d5db; background: linear-gradient(135deg, #f3f4f6, #e5e7eb); }

.ac-compact-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.ac-compact-cat {
  font-size: 0.6rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
  color: var(--ac-red); text-decoration: none;
}
.ac-compact-title-link { text-decoration: none; }
.ac-compact-title {
  font-size: 0.8125rem; font-weight: 600; line-height: 1.4; color: var(--ac-ink); margin: 0;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  transition: color var(--ac-t);
}
.ac-compact-title-link:hover .ac-compact-title { color: var(--ac-red); }
.ac-compact-meta { font-size: 0.65rem; color: var(--ac-ink3); }

/* ── Dark mode ── */
@media (prefers-color-scheme: dark) {
  :root {
    --ac-ink:    #f1f1f1;
    --ac-ink2:   #9ca3af;
    --ac-ink3:   #6b7280;
    --ac-bg:     #111;
    --ac-bg2:    #1a1a1a;
    --ac-border: rgba(255,255,255,.08);
  }
  .ac-card        { box-shadow: 0 1px 3px rgba(0,0,0,.3), 0 1px 2px rgba(0,0,0,.2); }
  .ac-card:hover  { box-shadow: 0 12px 40px rgba(0,0,0,.5), 0 4px 12px rgba(0,0,0,.3); }
  .ac-card-cover--fallback { background: linear-gradient(135deg, #1f2937, #111827); }
  .ac-hero-img--fallback   { background: #0f172a; }
  .ac-list-thumb--fallback { background: linear-gradient(135deg, #1f2937, #111827); }
  .ac-compact-thumb--fallback { background: linear-gradient(135deg, #1f2937, #111827); }
  .ac-tag         { background: #1a1a1a; }
  .ac-tag:hover   { background: #1e0f0f; }
  .ac-cat-badge   { background: rgba(230,51,17,.12); }
  .ac-tr-pill--done    { background: #14532d; color: #86efac; }
  .ac-tr-pill--pending { background: #451a03; color: #fbbf24; }
  .ac-tr-pill--missing { background: #1a1a1a; }
  .ac-tr-pill--loading { background: #1a1a1a; }
  .ac-tr-error    { background: #2a1111; border-color: #7f1d1d; }
  .ac-bookmark-btn { background: #1a1a1a; }
  .ac-bookmark-btn:hover { background: #1e0f0f; }
  .ac-bookmark-btn--on { background: #1e0f0f; }
  .ac-author-initials { background: linear-gradient(135deg, #c4280d, #d9440a); }
  .ac-list { box-shadow: none; border-color: rgba(255,255,255,.07); }
  .ac-compact:hover { background: #1a1a1a; }
  .ac-tr-request-btn { background: #1a1a1a; }
  .ac-tr-request-btn:hover:not(:disabled) { background: #1e0f0f; }
}
`;

export default ArticleCard;